// src/pages/WasteCodesPage.js
import React, { useState, useEffect, useContext } from 'react';
import './WasteCodesPage.css';
import ewcCodes from '../data/ewc-codes.json';
import BackToDashboard from "../components/BackToDashboard";
import AuthContext from '../AuthContext';
import API_BASE from '../apiConfig';

/* ---------- Helpers ---------- */

// Normalisera till jämförelse-nyckel (utan mellanrum, lower)
function canonKey(code = '') {
  return String(code).replace(/\s+/g, '').toLowerCase();
}

// Plocka ev. subnummer från beskrivningen (t.ex. "01*" i början)
function extractSubFromDesc(desc = '') {
  const m = String(desc).trim().match(/^(\d{2}\*?)(\b|[:\s])/);
  return m ? m[1] : null; // "01" eller "01*"
}

// Formatera till "NN NN NN" + ev "*"
function formatFullCode(baseCode = '', sub = null) {
  const base = String(baseCode).replace(/\s+/g, ' ').trim();
  const parts = base.split(' ').filter(Boolean); // ["05","01"] eller ["06","07"]
  let out = parts.join(' ');
  if (sub && parts.length === 2) out = `${parts[0]} ${parts[1]} ${sub}`;
  return out;
}

// Beräkna "displayCode" + dedupe-nyckel för ett item
function computeDisplay(item) {
  const sub = extractSubFromDesc(item.description);
  const displayCode = formatFullCode(item.code, sub);
  const key = canonKey(displayCode);
  return { displayCode, key };
}

// Ta bara ut siffror ur en kod
function getEwcDigits(code) {
  return String(code || '').replace(/\D/g, '');
}

// --------- Kategorisering (kapitel / underkapitel / special) ---------

// Kapitel (NN)
const CHAPTER = {
  '01': 'Gruv & mineral',
  '02': 'Jordbruk & livsmedel',
  '03': 'Trä & papper',
  '04': 'Läder & textil',
  '05': 'Petroleum',
  '06': 'Oorganisk kemi',
  '07': 'Organisk kemi',
  '08': 'Färg/lim/tätningsmedel',
  '09': 'Fotografi',
  '10': 'Termiska processer',
  '11': 'Metallytbehandling',
  '12': 'Metallbearbetning',
  '13': 'Oljor & bränslen',
  '14': 'Lösningsmedel/kylmedel',
  '15': 'Förpackningar/absorbenter',
  '16': 'Övrigt (WEEE, fordon, batterier)',
  '17': 'Bygg & rivning',
  '18': 'Hälso-/djurvård',
  '19': 'Avfallsbehandling/vatten',
  '20': 'Kommunalt avfall',
};

// Underkapitel (NN NN)
const SUBCHAPTER = {
  '02 01': 'Jordbruk & fiske',
  '02 02': 'Kött/fisk (animaliskt)',
  '02 03': 'Frukt/grönt & oljor',
  '02 04': 'Socker',
  '02 05': 'Mejeri',
  '02 06': 'Bageri & konfektyr',
  '02 07': 'Drycker',
  '15 01': 'Förpackningar',
  '16 01': 'Fordon',
  '16 02': 'El & elektronik (WEEE)',
  '16 06': 'Batterier',
  '17 01': 'Mineral (betong/tegel)',
  '17 02': 'Trä/glas/plast (B&R)',
  '17 03': 'Bitumen/asfalt',
  '17 04': 'Metaller (B&R)',
  '17 05': 'Jord & sten',
  '17 06': 'Isolering',
  '17 09': 'Blandat B&R',
  '20 01': 'Sorterade fraktioner',
  '20 02': 'Park & trädgård',
  '20 03': 'Blandat kommunalt',
};

// Kod-specifika taggar
const SPECIAL = {
  '20 01 01': 'Pappp/återvinning',
  '20 01 02': 'Glas',
  '20 01 36': 'El & elektronik (kommunalt)',
};

// Synonymer för sök
const SYNONYMS = {
  'El & elektronik (WEEE)': ['el','elektronik','weee','ee-avfall','apparat','kabel','tv','dator'],
  'El & elektronik (kommunalt)': ['el','elektronik','apparat','tv','dator'],
  Förpackningar: ['förpackning','emballage'],
  Glas: ['glas','flaska','ruta'],
  Batterier: ['batteri','akku','ackumulator'],
  Fordon: ['bil','fordon','vrak','bilbatteri','bilelektronik'],
  Metaller: ['metall','järn','stål','aluminium','koppar'],
  'Metaller (B&R)': ['metall','järn','stål','aluminium','koppar'],
  'Trä & papper': ['trä','virke','papper','papp','cellulosa'],
  'Trä/glas/plast (B&R)': ['trä','glas','plast','bygg','rivning'],
  'Mineral (betong/tegel)': ['betong','tegel','kakel','keramik'],
  'Jord & sten': ['jord','sten','schakt','fyllnad'],
  Mejeri: ['mejeri','mjölk','ost','yoghurt'],
  'Kött/fisk (animaliskt)': ['kött','slakt','fisk','animaliskt'],
  'Frukt/grönt & oljor': ['frukt','grönsaker','spannmål','oljor','vegetabiliskt'],
  'Bageri & konfektyr': ['bageri','bröd','konditori','konfektyr','bakverk'],
  'Park & trädgård': ['park','trädgård','gräs','löv'],
  'Sorterade fraktioner': ['sorterat','insamlat','fraktion'],
  'Blandat kommunalt': ['restavfall','hushåll','blandat'],
};

// Hjälpare för kapitel/underkapitel
function firstTwo(code=''){ const m = String(code).match(/^(\d{2})/); return m?m[1]:''; }
function firstTwoTwo(code=''){ const m = String(code).match(/^(\d{2}\s\d{2})/); return m?m[1]:''; }

// Kategori-etikett
function categoryLabel(displayCode='') {
  const full = String(displayCode).trim();
  if (SPECIAL[full]) return SPECIAL[full];
  const sub = firstTwoTwo(full);
  if (SUBCHAPTER[sub]) return SUBCHAPTER[sub];
  const ch = firstTwo(full);
  return CHAPTER[ch] || '';
}

// Normalisera text
function normalizeText(s=''){
  return String(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'');
}
function categorySearchText(displayCode=''){
  const label = categoryLabel(displayCode);
  const syns = SYNONYMS[label] || [];
  return [label, ...syns].join(' ');
}

/**
 * Bygg en lista med bara riktiga 6-siffriga koder.
 * Viktigt: vi filtrerar på displayCode (t.ex. "06 07 01*"),
 * inte på ursprungliga item.code ("06 07"), annars
 * försvinner t.ex. 06 07 01* och 16 06 01*.
 */
const EWC_CODES_6DIGIT = ewcCodes
  .map((item) => {
    const { displayCode, key } = computeDisplay(item);
    const digits = getEwcDigits(displayCode);
    if (digits.length !== 6) return null;
    return { ...item, displayCode, key };
  })
  .filter(Boolean);

export default function WasteCodesPage() {
  const { currentCompany, user: ctxUser } = useContext(AuthContext);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [savedCodes, setSavedCodes] = useState([]);

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  

  const effectiveCompanyId = React.useMemo(() => {
    const fromUser = toNum(ctxUser?.company_id);
    if (fromUser) return fromUser;

    const fromCtx = toNum(currentCompany?.id);
    if (fromCtx) return fromCtx;

    try {
      const raw = localStorage.getItem('currentCompany');
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return toNum(obj?.id) || null;
    } catch {
      return null;
    }
  }, [ctxUser?.company_id, currentCompany?.id]);

  // 🔹 NYTT: per-företag & per-användare-nyckel för dolda koder
  const [hiddenCodeKeys, setHiddenCodeKeys] = useState([]);

  const nsCompany = effectiveCompanyId ?? currentCompany?.id ?? 'anonCompany';
  const nsUser = ctxUser?.id
    ?? (ctxUser?.email ? String(ctxUser.email).toLowerCase() : 'anonUser');

  const hiddenCodesKey = React.useMemo(
    () => `wastecodes:hidden:${nsCompany}:${nsUser}`,
    [nsCompany, nsUser]
  );

  // 🔹 Läs in hidden-koder från localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(hiddenCodesKey);
      if (!raw) {
        setHiddenCodeKeys([]);
        return;
      }
      const arr = JSON.parse(raw);
      setHiddenCodeKeys(Array.isArray(arr) ? arr : []);
    } catch {
      setHiddenCodeKeys([]);
    }
  }, [hiddenCodesKey]);

  
  // === Ladda sparade koder från servern ===
  useEffect(() => {
    async function loadSavedCodes() {
      try {
        if (!effectiveCompanyId) return;

        const res = await fetch(
          `${API_BASE}/company-settings?companyId=${effectiveCompanyId}`,
          { credentials: 'include' }
        );
        const data = await res.json();

        if (!res.ok) {
          console.warn('[WasteCodes] kunde inte hämta company-settings:', data?.error);
          return;
        }

        const list = Array.isArray(data.saved_waste_codes)
          ? data.saved_waste_codes
          : [];

        // Se till att varje item har displayCode & key
        const normalized = list.map((it) => {
          const { displayCode, key } = computeDisplay(it);
          return { ...it, displayCode, key };
        });

        setSavedCodes(normalized);
      } catch (e) {
        console.warn('[WasteCodes] error loadSavedCodes:', e);
      }
    }

    loadSavedCodes();
  }, [effectiveCompanyId]);

// === Spara lista till servern ===
async function persistCodes(nextList) {
  setSavedCodes(nextList);

  try {
    const companyId = effectiveCompanyId;
    if (!companyId) {
      console.warn('[WasteCodes] inget companyId, sparar inte');
      return;
    }

    const payloadList = nextList.map((it) => ({
      code: it.code,
      description: it.description,
    }));

    console.log('[WasteCodes] sparar koder →', { companyId, payloadList });

    const res = await fetch(`${API_BASE}/company-settings/waste-codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        companyId,
        saved_waste_codes: payloadList,
      }),
    });

    const json = await res.json().catch(() => null);
    console.log('[WasteCodes] svar från /company-settings/waste-codes:', res.status, json);

    if (!res.ok || json?.ok === false) {
      console.warn(
        '[WasteCodes] kunde inte spara waste-codes:',
        json?.error || res.status
      );
    }
  } catch (e) {
    console.warn('[WasteCodes] nätverksfel vid sparning:', e);
  }
}

  // === Sök ===
  useEffect(() => {
    const trimmed = query.trim();
    const compactQ = trimmed.replace(/\s+/g, '').replace(/\*/g, '').toLowerCase();
    if (!compactQ) { setResults([]); return; }

    const hasLetters = /[a-zA-Z\u00C0-\u017F]/.test(trimmed);

    if (hasLetters) {
      const terms = normalizeText(trimmed).split(/\s+/).filter(Boolean);
      const scored = [];
      for (const item of EWC_CODES_6DIGIT) {
        const descN = normalizeText(item.description);
        const codeN = normalizeText(item.displayCode);
        const catN  = normalizeText(categorySearchText(item.displayCode));

        let score = 0, all = true;
        for (const t of terms) {
          let s = 0;
          if (catN.includes(t))  s = Math.max(s, 3);
          if (codeN.includes(t)) s = Math.max(s, 2);
          if (descN.includes(t)) s = Math.max(s, 1);
          if (s === 0) { all = false; break; }
          score += s;
        }
        if (all) scored.push({ ...item, score });
      }
      scored.sort((a,b) => b.score - a.score || a.displayCode.localeCompare(b.displayCode));

      // dedupe
      const seen = new Set(); const out = [];
      for (const it of scored) { if (seen.has(it.key)) continue; seen.add(it.key); out.push(it); }
      setResults(out);
    } else {
      // siffersök
      const filtered = EWC_CODES_6DIGIT.filter((item) => {
        const compactDisplay = item.displayCode.replace(/\s+/g, '').replace(/\*/g, '').toLowerCase();
        return compactDisplay.startsWith(compactQ);
      });
      const seen = new Set(); const out = [];
      for (const item of filtered) {
        if (seen.has(item.key)) continue; seen.add(item.key);
        out.push(item);
      }
      setResults(out);
    }
  }, [query]);

  // Toggle favorit (sparar till server)
    // Toggle favorit (sparar till server + hanterar hidden-lista)
  function handleSaveOrRemove(viewItem) {
    const item = viewItem.displayCode
      ? viewItem
      : { ...viewItem, ...computeDisplay(viewItem) };

    const codeDigits = getEwcDigits(item.displayCode || item.code);
    if (!codeDigits) return;

    const existsGlobally = savedCodes.some(
      (c) => getEwcDigits(c.displayCode || c.code) === codeDigits
    );
    const isHiddenForUser = hiddenCodeKeys.includes(codeDigits);

    // 🔹 FALL 1: Spara / återskapa (kod saknas globalt ELLER är bara dold för dig)
    if (!existsGlobally || isHiddenForUser) {
      let nextList = savedCodes;

      // om den inte finns i company-listan → lägg till den
      if (!existsGlobally) {
        const { displayCode, key } = computeDisplay(item);
        const newItem = {
          code: item.code,
          description: item.description,
          displayCode,
          key,
        };
        nextList = [...savedCodes, newItem];
      }

      // plocka bort den ur hidden-listan för den här användaren
      const nextHidden = hiddenCodeKeys.filter((k) => k !== codeDigits);
      setHiddenCodeKeys(nextHidden);
      try {
        localStorage.setItem(hiddenCodesKey, JSON.stringify(nextHidden));
      } catch {}

      persistCodes(nextList);
      return;
    }

    // 🔹 FALL 2: Ta bort (globalt) + markera som dold för den här användaren
    const nextList = savedCodes.filter(
      (c) => getEwcDigits(c.displayCode || c.code) !== codeDigits
    );

    const nextHidden = Array.from(new Set([...hiddenCodeKeys, codeDigits]));
    setHiddenCodeKeys(nextHidden);
    try {
      localStorage.setItem(hiddenCodesKey, JSON.stringify(nextHidden));
    } catch {}

    persistCodes(nextList);
  }


  const trimmedRaw = query.trim();

  return (
    <div className="wastecodes-container">
      {/* Enhetlig “Tillbaka”-knapp */}
      <div style={{ marginBottom: "1rem" }}>
        <BackToDashboard label="← Tillbaka till Dashboard" />
      </div>

      <h2 className="wastecodes-title">Sök avfallskod</h2>

      <input
        type="text"
        className="wastecodes-input"
        placeholder="Skriv kod eller text (t.ex. glas, el, batterier, betong)…"
        value={query}
        onChange={(e)=>setQuery(e.target.value)}
      />

      <div className="results-list">
        {!trimmedRaw && <p className="no-input">Börja skriva för att se resultat…</p>}
        {trimmedRaw && results.length === 0 && (
          <p className="no-results">Inga träffar för “{trimmedRaw}”.</p>
        )}

                {results.map((item) => {
          const { displayCode, key } = item;

          const digits = getEwcDigits(displayCode);
          const existsGlobally = savedCodes.some(
            (c) => getEwcDigits(c.displayCode || c.code) === digits
          );
          const isHiddenForUser = hiddenCodeKeys.includes(digits);
          const exists = existsGlobally && !isHiddenForUser;

          const cat = categoryLabel(displayCode || item.code);


         const isHazardous =
  /\*/.test(String(displayCode || '')) ||
  /\*/.test(String(item.code || '')) ||
  /\*/.test(String(item.description || '')) ||
  /farligt avfall/i.test(String(item.description || ''));
  
          return (
            <div key={key} className="result-item">
              <div className="code-desc">
                <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                  <span className="code">{displayCode}</span>
                  {cat && <span className="sector-chip">{cat}</span>}
                  {isHazardous && (
                    <span className="hazard-chip">Farligt avfall</span>
                  )}
                </div>
                <span className="desc">{item.description}</span>
              </div>
              <button
                type="button"
                className={exists ? 'btn-remove' : 'btn-save'}
                onClick={() => handleSaveOrRemove(item)}
              >
                {exists ? 'Ta bort' : 'Spara'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
